import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { MdClose, MdCheck } from "react-icons/md";
import { getTagVoteCounts, submitTagVote, getUserVote } from "../../../../../services/TagsService";
import toast from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useMixpanelEvent from "../../../../../hooks/useMixpanelEvent";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import { useUser } from "../../../../../context/UserContext";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * TagVoteBar component displays voting options for a specific tag and allows users to vote yes/no.
 *
 * @param {Object} props
 * @param {Object} props.tag - The tag object being voted on
 * @param {string} props.taggableId - The ID of the taggable item
 * @param {string} props.tagId - The ID of the tag
 * @param {string} props.userId - The ID of the current user
 * @param {boolean} props.hideVoteButtons - If true, hide the vote buttons
 * @param {string} props.propertyId - The ID of the current property being voted on.
 */
const TagVoteBar = ({ tag, taggableId, tagId, userId, hideVoteButtons, propertyId }) => {
    const queryClient = useQueryClient();
    const [voteCounts, setVoteCounts] = useState({ agree: 0, disagree: 0 });
    const [userVote, setUserVote] = useState(null);
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const [socialState] = useLocalStorage("social-state");
    const { userState } = useUser();
    const { isAuthenticated } = useAuth0();
    const isListingPage = window.location.pathname.includes("/listing/");

    const refetchQueries = () => {
        queryClient.invalidateQueries(["tag-votes", taggableId, tagId]);
        queryClient.invalidateQueries(["user-vote", taggableId, tagId, userId]);
        queryClient.invalidateQueries(["comments", propertyId, tagId]);
    };

    const { data: voteCountsData, isLoading: isVotesLoading } = useQuery({
        queryKey: ["tag-votes", taggableId, tagId, userId],
        queryFn: () => getTagVoteCounts(taggableId, tagId, userId),
        enabled: Boolean(taggableId && tagId),
    });

    const { data: userVoteData, isLoading: isUserVoteLoading } = useQuery({
        queryKey: ["user-vote", taggableId, tagId, userId],
        queryFn: () => getUserVote(taggableId, tagId, userId),
        enabled: Boolean(taggableId && tagId && userId),
    });

    // Update local states when server data changes
    useEffect(() => {
        if (voteCountsData?.data) {
            setVoteCounts({
                agree: voteCountsData.data.agree || 0,
                disagree: voteCountsData.data.disagree || 0,
            });
        }
    }, [voteCountsData?.data]);

    useEffect(() => {
        if (userVoteData?.voteType) {
            setUserVote(userVoteData.voteType);
        }
    }, [userVoteData?.voteType]);

    const voteMutation = useMutation({
        mutationFn: ({ voteType }) => submitTagVote(taggableId, tagId, userId, voteType),
        onMutate: async (newVote) => {
            const newVoteCounts = { ...voteCounts };

            // Update counts based on vote change
            if (userVote) newVoteCounts[userVote]--;
            if (newVote.voteType !== "none") newVoteCounts[newVote.voteType]++;

            // Optimistic update
            setUserVote(newVote.voteType);
            setVoteCounts(newVoteCounts);
        },
        onError: (err) => {
            toast.error(err.message);
            refetchQueries();
        },
        onSuccess: () => {
            refetchQueries();

            // Track Vote Hashtag event on successful vote
            let pageName = "Property Feed - Trending";
            if (isListingPage) {
                pageName = "Property Details";
            } else if (socialState?.activeTab === "following") {
                pageName = "Property Feed - Following";
            }

            sendMixpanelEvent("Vote Hashtag", {
                pageName,
                hashtag: tag.name,
                user_id: userId,
                nickname: userState?.nickname,
                email: userState?.email,
                isAuthenticated
            });
        },
    });

    const handleVote = async (voteType) => {
        voteMutation.mutate({ voteType });
    };

    if (!taggableId || !tagId || !userId) {
        return null;
    }

    if (isVotesLoading || isUserVoteLoading) {
        return (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-mt-2">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 tw-text-[18px] tw-font-semibold tw-w-full tw-relative">
                    <div className="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-24"></div>
                    <div className="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-32"></div>
                    <div className="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-16"></div>
                </div>
                <div className="tw-flex tw-gap-2 tw-mt-2 tw-w-full">
                    <div className="tw-flex-1 tw-h-10 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                    <div className="tw-flex-1 tw-h-10 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                </div>
                <div className="tw-w-full tw-mt-3 tw-mb-1">
                    <div className="tw-w-full tw-h-2 tw-bg-gray-200 tw-rounded-full tw-animate-pulse"></div>
                </div>
                <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                    <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-24"></div>
                    <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-24"></div>
                </div>
            </div>
        );
    }

    const total = voteCounts.disagree + voteCounts.agree;
    const noPercent = total > 0 ? (voteCounts.disagree / total) * 100 : 0;
    const yesPercent = total > 0 ? (voteCounts.agree / total) * 100 : 0;

    return (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-mt-2">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 tw-text-[18px] tw-font-semibold tw-w-full tw-relative">
                {hideVoteButtons ? (
                    <span className="tw-text-[#6C26C4]">#{tag.name}</span>
                ) : (
                    <>
                        <span>Do you agree with the</span>
                        <span className="tw-text-[#6C26C4]">#{tag.name}</span>
                        <span>tag?</span>
                    </>
                )}
            </div>
            {!hideVoteButtons && (
                <div className="tw-flex tw-gap-2 tw-mt-2 tw-w-full">
                    <button
                        className={
                            userVote === "disagree"
                                ? "tw-flex-1 tw-flex tw-items-center tw-justify-center tw-bg-[#DB2528] tw-text-white tw-font-bold tw-py-2 tw-rounded-lg tw-text-base tw-gap-2"
                                : "tw-flex-1 tw-flex tw-items-center tw-justify-center tw-bg-[#F3F4F6] tw-text-black tw-font-bold tw-py-2 tw-rounded-lg tw-text-base tw-gap-2 tw-border-[1px] tw-border-solid tw-border-[#D9D9D9]"
                        }
                        onClick={() => handleVote("disagree")}
                        disabled={voteMutation.isLoading}
                    >
                        <MdClose
                            className={
                                userVote === "disagree"
                                    ? "tw-text-white tw-text-xl"
                                    : "tw-text-black tw-text-xl"
                            }
                        />{" "}
                        Nope
                    </button>
                    <button
                        className={
                            userVote === "agree"
                                ? "tw-flex-1 tw-flex tw-items-center tw-justify-center tw-bg-[#55A809] tw-text-white tw-font-bold tw-py-2 tw-rounded-lg tw-text-base tw-gap-2"
                                : "tw-flex-1 tw-flex tw-items-center tw-justify-center tw-bg-[#F3F4F6] tw-text-black tw-font-bold tw-py-2 tw-rounded-lg tw-text-base tw-gap-2 tw-border-[1px] tw-border-solid tw-border-[#D9D9D9]"
                        }
                        onClick={() => handleVote("agree")}
                        disabled={voteMutation.isLoading}
                    >
                        <MdCheck
                            className={
                                userVote === "agree" ? "tw-text-white tw-text-xl" : "tw-text-black tw-text-xl"
                            }
                        />{" "}
                        Yep
                    </button>
                </div>
            )}
            <div className="tw-w-full tw-mt-3 tw-mb-1">
                <div className="tw-w-full tw-h-2 tw-bg-gray-200 tw-rounded-full tw-relative">
                    <div
                        className="tw-bg-[#DB2528] tw-h-2 tw-rounded-l-full tw-transition-all tw-duration-500 tw-ease-in-out"
                        style={{ width: `${noPercent}%` }}
                    />
                    <div
                        className="tw-bg-[#55A809] tw-h-2 tw-rounded-r-full tw-absolute tw-top-0 tw-transition-all tw-duration-500 tw-ease-in-out"
                        style={{ left: `${noPercent}%`, width: `${yesPercent}%` }}
                    />
                </div>
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-text-xs tw-font-normal tw-italic tw-mb-2 tw-text-black">
                <span>
                    {voteCounts.disagree.toLocaleString()} <span className="tw-font-bold not-italic">NO</span>{" "}
                    votes
                </span>
                <span>
                    {voteCounts.agree.toLocaleString()} <span className="tw-font-bold not-italic">YES</span>{" "}
                    votes
                </span>
            </div>
        </div>
    );
};

TagVoteBar.propTypes = {
    tag: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
    }).isRequired,
    taggableId: PropTypes.string.isRequired,
    tagId: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
    hideVoteButtons: PropTypes.bool,
    propertyId: PropTypes.string.isRequired,
};

export default TagVoteBar;
